import { useState, useEffect } from 'react';
import axios from 'axios';
import { cn } from '../lib/utils';
import { formatUK } from '../lib/ukTime';
import { ArrowUpRight, ArrowDownRight, MapPin, Image as ImageIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Green/red clock-in / clock-out box: exact time, photo thumbnail, GPS map link.
export function ClockPunchDetail({ kind, event, onViewPhoto }) {
  const isIn = kind === 'in';
  const label = isIn ? 'CLOCK IN' : 'CLOCK OUT';
  const tone = isIn
    ? 'border-green-500/30 bg-green-500/5 text-green-400'
    : 'border-red-500/30 bg-red-500/5 text-red-400';
  const isManual = event && (event.allocated_by || event.manual_clock_in || event.manual_clock_out || event.admin_override);
  const hasGps = event && event.latitude != null && event.longitude != null;

  return (
    <div className={cn('border rounded-sm p-2.5', event ? tone : 'border-border bg-muted/20')} data-testid={`punch-${kind}-detail`}>
      <div className="flex items-center justify-between gap-2">
        <span className={cn('text-[10px] font-barlow tracking-widest uppercase', event ? '' : 'text-muted-foreground')}>
          {isIn ? <ArrowUpRight className="w-3 h-3 inline mr-1" /> : <ArrowDownRight className="w-3 h-3 inline mr-1" />}
          {label}
        </span>
        {isManual && (
          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-500/15 text-amber-400 border border-amber-500/30">Manual</span>
        )}
      </div>
      {!event ? (
        <p className="text-xs text-muted-foreground mt-1.5">{isIn ? 'Not clocked in yet' : 'Not clocked out yet'}</p>
      ) : (
        <div className="mt-1.5 flex items-start gap-2.5">
          {event.photo_url ? (
            <ClockPhotoThumb eventId={event.id} onExpand={() => onViewPhoto(event.id)} />
          ) : (
            <div className="w-14 h-14 flex-shrink-0 rounded-sm bg-muted/40 border border-border flex items-center justify-center">
              <ImageIcon className="w-4 h-4 text-muted-foreground/40" />
            </div>
          )}
          <div className="min-w-0 space-y-1">
            <p className="font-mono text-sm text-foreground">{formatUK(event.timestamp, 'HH:mm:ss')}</p>
            {hasGps ? (
              <a
                href={`https://www.google.com/maps?q=${event.latitude},${event.longitude}`}
                target="_blank" rel="noopener noreferrer"
                className="text-xs text-blue-400 hover:text-blue-300 hover:underline flex items-center gap-1"
                data-testid={`punch-${kind}-map-link`}
              >
                <MapPin className="w-3 h-3 flex-shrink-0" />
                {Number(event.latitude).toFixed(5)}, {Number(event.longitude).toFixed(5)}
              </a>
            ) : (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <MapPin className="w-3 h-3" /> No GPS
              </p>
            )}
            {!event.photo_url && <p className="text-[10px] text-muted-foreground">No photo</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export function ClockPhotoThumb({ eventId, onExpand }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/admin/clock/photo/${eventId}`)
      .then((r) => {
        if (mounted && r.data.photo_url && r.data.photo_url !== 'has_photo') setSrc(r.data.photo_url);
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, [eventId]);

  if (!src) {
    return (
      <div className="w-14 h-14 flex-shrink-0 rounded-sm bg-muted/40 border border-border flex items-center justify-center animate-pulse">
        <ImageIcon className="w-4 h-4 text-muted-foreground/40" />
      </div>
    );
  }
  return (
    <button type="button" onClick={onExpand} className="flex-shrink-0 group" title="Click to enlarge" data-testid={`punch-photo-${eventId}`}>
      <img src={src} alt="Clock" className="w-14 h-14 object-cover rounded-sm border border-border group-hover:opacity-80 group-hover:ring-2 group-hover:ring-primary transition-all" />
    </button>
  );
}

export default ClockPunchDetail;
